/** 4.1 Button: primary (sand bead), digu (ochre, hold to confirm), quiet, ghost, off (with its reason). */
import { useCallback, useEffect, useRef, useState, type CSSProperties, type MouseEvent, type PointerEvent, type ReactElement, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Icon } from "./Icon";
import { haptic } from "../lib/haptics";

export type ButtonKind = "primary" | "digu" | "quiet" | "danger" | "ghost" | "off";
export type ButtonSize = 54 | 52 | 50 | 48 | 46 | 44;
export const HOLD_MS = 600;

interface Base {
  children: ReactNode;
  size?: ButtonSize;
  icon?: string;
  /** Required for `off`; printed 8 px beneath in small/text-muted. Allowed for any kind (waiting reasons). */
  reason?: string;
  full?: boolean;
  type?: "button" | "submit";
  className?: string;
  style?: CSSProperties;
  to?: string;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  /** Hold-to-confirm: fires after `holdMs` (600) of continuous press; releasing earlier cancels. */
  onHold?: () => void;
  holdMs?: number;
  busy?: boolean;
  id?: string;
  "aria-label"?: string;
  /** Stable id for usage tracking (D-075, lib/track.ts). */
  track?: string;
}
export type ButtonProps = (Base & { kind?: Exclude<ButtonKind, "off"> }) | (Base & { kind: "off"; reason: string });

const sizeClass: Record<ButtonSize, string> = { 54: "", 52: "btn-c--52", 50: "btn-c--50", 48: "btn-c--s", 46: "btn-c--46", 44: "btn-c--44" };

export function Button(props: ButtonProps): ReactElement {
  const { children, kind = "primary", size = 54, icon, reason, full = true, type = "button", className = "", style, to, onClick, onHold, holdMs = HOLD_MS, busy = false, id, track } = props;
  const off = kind === "off";
  const [progress, setProgress] = useState(0);
  const [holding, setHolding] = useState(false);
  const raf = useRef<number | null>(null);
  const start = useRef(0);
  const done = useRef(false);
  const active = useRef(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  const stop = useCallback(() => { active.current = false; if (raf.current) cancelAnimationFrame(raf.current); raf.current = null; setHolding(false); setProgress(0); }, []);
  useEffect(() => stop, [stop]);

  const onDown = (e: PointerEvent<HTMLButtonElement>) => {
    if (!onHold || off || busy) return;
    e.preventDefault();
    btnRef.current?.setPointerCapture?.(e.pointerId);
    done.current = false; active.current = true; start.current = performance.now(); setHolding(true);
    const tick = (t: number) => {
      if (!active.current) return;
      const p = Math.min(1, (t - start.current) / holdMs);
      setProgress(p);
      if (p >= 1) { done.current = true; stop(); haptic("confirm"); onHold(); }
      else raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
  };
  const onUp = () => { if (onHold && !done.current) stop(); };
  const onKey = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!onHold || off) return;
    if ((e.key === " " || e.key === "Enter") && !e.repeat && !holding) { e.preventDefault(); onDown({ preventDefault() {}, pointerId: -1 } as unknown as PointerEvent<HTMLButtonElement>); }
  };
  const onKeyUp = (e: React.KeyboardEvent<HTMLButtonElement>) => { if (onHold && (e.key === " " || e.key === "Enter")) onUp(); };

  const iconSize = size === 54 ? 16 : 14;
  const r = size / 2;
  const label = (
    <span className="btn-c__label" style={holding && isReduced() ? { opacity: 1 - Math.floor(progress * 3) * 0.15 } : undefined}>
      {icon && <Icon name={icon} size={iconSize} stroke={2.4} />}
      <span className="truncate">{busy ? "One moment…" : children}</span>
    </span>
  );
  const cls = `btn-c btn-c--${kind} ${sizeClass[size]} ${full ? "" : "btn-c--auto"} ${holding ? "btn-c--holding" : ""} ${className}`;
  const styles: CSSProperties = { ...style, ["--btn-r" as string]: `${r}px` };
  const ring = holding && !isReduced() && (
    <svg className="btn-c__ring" aria-hidden="true" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ opacity: 0.9 }}>
      <rect x="3" y="3" width="94" height="88" rx={r - 3} ry={r - 3} pathLength={100} strokeDasharray="100" strokeDashoffset={100 - progress * 100} vectorEffect="non-scaling-stroke" />
    </svg>
  );

  let el: ReactElement;
  if (to && !off && !onHold) {
    el = <Link to={to} className={cls} style={styles} id={id} data-track={track} aria-label={props["aria-label"]} onClick={onClick as (e: MouseEvent<HTMLAnchorElement>) => void}>{label}</Link>;
  } else {
    el = (
      <button
        data-track={track}
        ref={btnRef} type={type} className={cls} style={styles} id={id} aria-label={props["aria-label"]}
        aria-disabled={off || busy || undefined} aria-busy={busy || undefined} data-hold={onHold ? holdMs : undefined} data-progress={holding ? progress.toFixed(2) : undefined}
        onClick={(e) => { if (off || busy) { e.preventDefault(); return; } if (onHold) { e.preventDefault(); return; } onClick?.(e); }}
        onPointerDown={onDown} onPointerUp={onUp} onPointerCancel={onUp} onPointerLeave={onUp} onKeyDown={onKey} onKeyUp={onKeyUp} onContextMenu={onHold ? (e) => e.preventDefault() : undefined}
      >
        {ring}{label}
      </button>
    );
  }
  if (!reason) return el;
  return (
    <div className={`btn-wrap ${full ? "" : "btn-wrap--auto"}`}>
      {el}
      <p className="btn-reason small">{reason}</p>
    </div>
  );
}

function isReduced(): boolean { return typeof document !== "undefined" && document.documentElement.dataset.reduceMotion === "true"; }
